import { CalendarEvent } from "../types";

export async function fetchCalendarEvents(accessToken: string, days: number = 14): Promise<CalendarEvent[]> {
  const timeMin = new Date();
  timeMin.setDate(timeMin.getDate() - days);
  
  const timeMax = new Date();
  timeMax.setDate(timeMax.getDate() + 7); // Show a bit into the future too

  const url = `https://www.googleapis.com/calendar/v3/calendars/primary/events?timeMin=${timeMin.toISOString()}&timeMax=${timeMax.toISOString()}&orderBy=startTime&singleEvents=true&maxResults=50`;

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` }
  });

  if (!response.ok) {
    let errorDetail = "";
    try {
      const errorJson = await response.json();
      errorDetail = errorJson.error?.message || JSON.stringify(errorJson);
    } catch (e) {
      errorDetail = await response.text();
    }
    
    if (response.status === 403) {
      throw new Error(`Calendar API Access Denied (403): ${errorDetail}. Please ensure the 'Google Calendar API' is enabled in your Google Cloud Console.`);
    } else if (response.status === 401) {
      throw new Error(`Unauthorized (401): Your session may have expired. Please try re-connecting.`);
    }
    
    throw new Error(`Failed to fetch Calendar events (${response.status}): ${errorDetail}`);
  }

  const data = await response.json();
  if (!data.items) return [];

  return data.items
    .filter((item: any) => !['workingLocation', 'outOfOffice', 'focusTime'].includes(item.eventType))
    .map((item: any) => ({
      id: item.id,
      title: item.summary || "Untitled Event",
      startTime: item.start?.dateTime || item.start?.date || "",
      endTime: item.end?.dateTime || item.end?.date || "",
      attendees: item.attendees?.map((a: any) => a.email || a.displayName).filter(Boolean) || [],
      description: item.description,
      meetLink: item.hangoutLink
    }));
}

export async function updateCalendarEventDescription(accessToken: string, eventId: string, newDescription: string): Promise<void> {
  const url = `https://www.googleapis.com/calendar/v3/calendars/primary/events/${eventId}`;

  const response = await fetch(url, {
    method: 'PATCH',
    headers: { 
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      description: newDescription
    })
  });

  if (!response.ok) {
    let errorDetail = "";
    try {
      const errorJson = await response.json();
      errorDetail = errorJson.error?.message || JSON.stringify(errorJson);
    } catch (e) {
      errorDetail = await response.text();
    }
    throw new Error(`Failed to update Calendar event (${response.status}): ${errorDetail}`);
  }
}
